import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { PATH } from 'app.routes.const';
import { dateHelper, enumHelper, isSkeletonLoading, ORDER_STATUSES, NumberFormatCustom } from 'shared';
import { FILTER_TYPE } from 'shared/ViewTable/AdvancedFilters';

export function useOrdersBuildTable(orders, t) {
  const history = useHistory();
  const orderStatusesElements = useMemo(() => {
    return enumHelper.enumToFilter(ORDER_STATUSES, t, 'statuses');
  }, [t]);

  const headCells = useMemo(() => {
    return [
      { id: 'installation', numeric: false, disablePadding: false, label: t('properties.installation') },
      {
        id: 'status',
        numeric: false,
        disablePadding: false,
        label: t('properties.status'),
        filterType: FILTER_TYPE.COMBO,
        filterValues: orderStatusesElements
      },
      {
        id: 'entryDate',
        numeric: false,
        disablePadding: false,
        label: t('properties.entryDate'),
        filterType: FILTER_TYPE.DATE
      },
      { id: 'experience', numeric: false, disablePadding: false, label: t('properties.experience') },
      { id: 'customerReference', numeric: false, disablePadding: false, label: t('properties.customerReference') },
      { id: 'tagAmount', numeric: true, disablePadding: false, label: t('properties.tagAmount') },
      { id: 'edit', action: true }
    ];
  }, [t, orderStatusesElements]);

  const exportHeadCells = useMemo(() => {
    return [
      { id: 'installation', numeric: false, label: t('properties.installation') },
      { id: 'client', numeric: false, label: t('properties.client') },
      {
        id: 'status',
        label: t('properties.status'),
        translate: true,
        mapper: value =>
          value >= 0 && orderStatusesElements && orderStatusesElements.filter(f => f.value === value.toString())[0].name
      },
      { id: 'tagAmount', label: t('properties.tagAmount') },
      {
        id: 'entryDate',
        label: t('properties.entryDate'),
        mapper: value => value && dateHelper.toLongDate(value.toString())
      },
      { id: 'experience', label: t('properties.experience') },
      { id: 'customerReference', label: t('properties.customerReference') },
      { id: 'supplierOrder', label: t('properties.supplierOrder') },
      { id: 'supplierReference', label: t('properties.supplierReference') },
      {
        id: 'createdDate',
        label: t('properties.createdDate'),
        mapper: value => value && dateHelper.toLongDate(value.toString())
      },
      {
        id: 'pendingCodingDate',
        label: t('properties.pendingCodingDate'),
        mapper: value => value && dateHelper.toLongDate(value.toString())
      },
      {
        id: 'completedDate',
        label: t('properties.completedDate'),
        mapper: value => value && dateHelper.toLongDate(value.toString())
      }
    ];
  }, [t, orderStatusesElements]);

  const rows = useMemo(() => {
    if (!orders) {
      return [];
    }
    return orders.items.map(order => ({
      id: order.id,
      installation: order.installation,
      status: isSkeletonLoading(order.status)
        ? order.status
        : orderStatusesElements && orderStatusesElements.filter(f => f.value === order.status.toString())[0].name,
      entryDate: isSkeletonLoading(order.entryDate)
        ? order.entryDate
        : order.entryDate
          ? dateHelper.toLongDate(order.entryDate)
          : '',
      experience: order.experience,
      customerReference: order.customerReference,
      tagAmount: isSkeletonLoading(order.tagAmount)
        ? order.tagAmount
        : order.tagAmount && <NumberFormatCustom displayType={'text'} value={order.tagAmount} suffix="" />,
      edit: {
        title: t('actions.view'),
        cb: () => history.push(PATH.ORDERS.EDIT.replace(':id', order.id)),
        icon: <VisibilityIcon />
      }
    }));
  }, [history, orders, orderStatusesElements, t]);

  return { headCells, rows, exportHeadCells };
}
